import React, { useMemo, useRef, useState, useEffect } from "react";
import { academicCalendarApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import "./AcademicCalendar.scss";

const EVENT_TYPES = [
  { value: "academic",  label: "Academic",         color: "#3b82f6" },
  { value: "exam",      label: "Examination",       color: "#8b5cf6" },
  { value: "holiday",   label: "Holiday",           color: "#10b981" },
  { value: "event",     label: "College Event",     color: "#f59e0b" },
  { value: "notice",    label: "Important Notice",  color: "#ef4444" },
  { value: "admission", label: "Admission",         color: "#0ea5e9" },
  { value: "result",    label: "Result",            color: "#ec4899" },
  { value: "other",     label: "Other",             color: "#64748b" },
];

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const defaultForm = {
  _id: null,
  Event_Title: "",
  Event_Type: "Academic",
  Start_Date: "",
  End_Date: "",
  Description: "",
  Image_URL: "",
  Location: "",
  Status: "Upcoming",
};

const pad = (n) => String(n).padStart(2, "0");
const toDateStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const formatDisplay = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const getTypeConfig = (type) =>
  EVENT_TYPES.find((t) => t.value === type) || EVENT_TYPES[EVENT_TYPES.length - 1];

const AcademicCalendar = () => {
  const [events, setEvents]             = useState([]);
  const [loading, setLoading]           = useState(false);
  const [view, setView]                 = useState("calendar");
  const [filterType, setFilterType]     = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMonth, setFilterMonth]   = useState("all");
  const [searchText, setSearchText]     = useState("");
  const [modalType, setModalType]       = useState("");
  const [formData, setFormData]         = useState(defaultForm);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [confirmId, setConfirmId]       = useState(null);
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]   = useState({ top: 0, left: 0 });
  const [calMonth, setCalMonth]         = useState(new Date().getMonth());
  const [calYear, setCalYear]           = useState(new Date().getFullYear());
  const [dayPopup, setDayPopup]         = useState(null);
  const dropdownRef  = useRef(null);
  const dayPopupRef  = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl]     = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const removePhoto = () => {
    setFormData(p => ({ ...p, Image_URL: "" }));
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const today = new Date();
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await apiClient(academicCalendarApi);
      const result = await response.json();
      if (response.ok) {
        setEvents(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = useMemo(() => {
    return events
      .filter((ev) => {
        const matchType   = filterType   === "all" || ev.Event_Type   === filterType;
        const matchStatus = filterStatus === "all" || ev.Status === filterStatus;
        const matchSearch = (ev.Event_Title || "").toLowerCase().includes(searchText.toLowerCase());
        const matchMonth  = filterMonth  === "all" || new Date(ev.Start_Date).getMonth() === parseInt(filterMonth);
        return matchType && matchStatus && matchSearch && matchMonth;
      })
      .sort((a, b) => new Date(a.Start_Date) - new Date(b.Start_Date));
  }, [events, filterType, filterStatus, searchText, filterMonth]);

  useEffect(() => {
    const onOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null);
      if (dayPopupRef.current && !dayPopupRef.current.contains(e.target)) setDayPopup(null);
    };
    const onClose = () => { setDropdownOpenId(null); setDayPopup(null); };
    document.addEventListener("mousedown", onOutside);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 190 });
    setDropdownOpenId(id);
  };

  const openAddModal  = (prefill = {}) => { 
    setFormData({ ...defaultForm, ...prefill }); 
    setSelectedFile(null);
    setPreviewUrl(null);
    setModalType("add"); 
    setDropdownOpenId(null); 
    setDayPopup(null); 
  };
  const openViewModal = (ev)            => { setSelectedEvent(ev); setModalType("view"); setDropdownOpenId(null); setDayPopup(null); };
  const openEditModal = (ev)            => { 
    setFormData({ 
      ...ev, 
      Start_Date: ev.Start_Date ? new Date(ev.Start_Date).toISOString().split('T')[0] : "", 
      End_Date: ev.End_Date ? new Date(ev.End_Date).toISOString().split('T')[0] : "" 
    }); 
    setSelectedFile(null);
    setPreviewUrl(null);
    setModalType("edit"); 
    setDropdownOpenId(null); 
    setDayPopup(null); 
  };
  const closeModal    = ()              => { 
    setModalType(""); 
    setSelectedEvent(null); 
    setFormData(defaultForm); 
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? academicCalendarApi : `${academicCalendarApi}/${formData._id}`;
      
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== 'Image_URL' && formData[key] !== null && formData[key] !== undefined) {
          if (key === '_id' && formData[key] === null) return;
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedFile) {
        formDataToSend.append('Image_URL', selectedFile);
      } else if (formData.Image_URL) {
        formDataToSend.append('Image_URL', formData.Image_URL);
      } else {
        formDataToSend.append('Image_URL', '');
      }

      const response = await apiFormDataClient(url, {
        method,
        body: formDataToSend
      });
      if (response.ok) { fetchEvents(); closeModal(); }
    } catch (error) {
      console.error("Error saving event:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${academicCalendarApi}/${id}`, { method: "DELETE" });
      if (response.ok) { fetchEvents(); setConfirmId(null); }
    } catch (error) {
      console.error("Error deleting event:", error);
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear((y) => y - 1); } else setCalMonth((m) => m - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0);  setCalYear((y) => y + 1); } else setCalMonth((m) => m + 1); };
  const goToday   = () => { setCalMonth(today.getMonth()); setCalYear(today.getFullYear()); };

  const buildCalendarDays = () => {
    const firstDay    = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = toDateStr(calYear, calMonth, day);
    return events
      .filter((ev) => {
        const sd = new Date(ev.Start_Date).toISOString().split('T')[0];
        const ed = new Date(ev.End_Date || ev.Start_Date).toISOString().split('T')[0];
        return dateStr >= sd && dateStr <= ed;
      })
      .sort((a, b) => new Date(a.Start_Date) - new Date(b.Start_Date));
  };

  const handleDayClick = (e, day) => {
    if (!day) return;
    e.stopPropagation();
    const rect    = e.currentTarget.getBoundingClientRect();
    const dateStr = toDateStr(calYear, calMonth, day);
    const dayEvs  = getEventsForDay(day);
    setDayPopup({ day, dateStr, events: dayEvs, anchorRect: rect });
  };

  const getPopupStyle = () => {
    if (!dayPopup) return {};
    const r = dayPopup.anchorRect;
    const popW = 300, popH = 320, gap = 8;
    let left = r.left, top = r.bottom + gap;
    if (left + popW > window.innerWidth - 12) left = r.right - popW;
    if (top + popH > window.innerHeight - 12) top = r.top - popH - gap;
    return { top, left };
  };

  return (
    <div className="acal-page">
      <div className="acal-header">
        <div><h1>Academic Calendar</h1><p>Manage academic events, holidays, and examinations.</p></div>
        <button type="button" className="acal-add-btn" onClick={() => openAddModal()}>+ Add Event</button>
      </div>

      <div className="acal-stats">
        <div className="stat-card total"><div className="stat-icon">📅</div><div className="stat-info"><span>Total</span><strong>{events.length}</strong></div></div>
        <div className="stat-card published"><div className="stat-icon">✅</div><div className="stat-info"><span>Published</span><strong>{events.filter(e => e.Status === "Upcoming").length}</strong></div></div>
        <div className="stat-card draft"><div className="stat-icon">📝</div><div className="stat-info"><span>Drafts</span><strong>{events.filter(e => e.Status === "Ongoing").length}</strong></div></div>
      </div>

      <div className="acal-filter-card">
        <div className="filter-top">
          <div className="acal-filter-grid">
            <input type="text" placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}><option value="all">All Types</option>{EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select>
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}><option value="all">All Months</option>{MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}</select>
          </div>
          <div className="view-toggle">
            <button className={view === "calendar" ? "active" : ""} onClick={() => setView("calendar")}>Calendar</button>
            <button className={view === "list" ? "active" : ""} onClick={() => setView("list")}>List</button>
          </div>
        </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      {view === "calendar" ? (
        <div className="acal-calendar-card">
          <div className="cal-nav">
            <button onClick={prevMonth}>&#8249;</button>
            <h3>{MONTHS[calMonth]} {calYear}</h3>
            <button onClick={nextMonth}>&#8250;</button>
            <button onClick={goToday} className="cal-today-btn">Today</button>
          </div>
          <div className="cal-grid">
            {WEEKDAYS.map(d => <div key={d} className="cal-day-label">{d}</div>)}
            {buildCalendarDays().map((day, idx) => {
              const dayEvs = getEventsForDay(day);
              const dateStr = day ? toDateStr(calYear, calMonth, day) : null;
              return (
                <div key={idx} className={`cal-cell ${!day ? "empty" : "clickable"} ${dateStr === todayStr ? "today" : ""}`} onClick={(e) => handleDayClick(e, day)}>
                  {day && (
                    <>
                      <div className="cal-cell-head"><span>{day}</span>{dayEvs.length > 0 && <span className="cal-ev-count">{dayEvs.length}</span>}</div>
                      <div className="cal-events">
                        {dayEvs.slice(0, 2).map(ev => <div key={ev._id} className="cal-event-pill" style={{ "--ev-color": getTypeConfig(ev.Event_Type).color }}>{ev.Event_Title}</div>)}
                        {dayEvs.length > 2 && <div className="cal-more-tag">+{dayEvs.length - 2} more</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="acal-table-card">
          <table>
            <thead><tr><th>Event</th><th>Type</th><th>Start Date</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredEvents.map(ev => (
                <tr key={ev._id}>
                  <td><strong>{ev.Event_Title}</strong></td>
                  <td>{getTypeConfig(ev.Event_Type).label}</td>
                  <td>{formatDisplay(ev.Start_Date)}</td>
                  <td><span className={`status-badge ${ev.Status}`}>{ev.Status}</span></td>
                  <td><button onClick={(e) => handleDotsClick(e, ev._id)}>⋮</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dayPopup && (
        <div ref={dayPopupRef} className="day-popup" style={{ position: "fixed", zIndex: 8888, ...getPopupStyle() }}>
          <div className="day-popup-header"><span>{dayPopup.dateStr}</span><button onClick={() => setDayPopup(null)}>×</button></div>
          <div className="day-popup-events">
            {dayPopup.events.map(ev => (
              <div key={ev._id} className="popup-event-row" onClick={() => openViewModal(ev)}>
                <strong>{ev.Event_Title}</strong><br/><small>{getTypeConfig(ev.Event_Type).label}</small>
              </div>
            ))}
            <button className="popup-add-btn" onClick={() => openAddModal({ Start_Date: dayPopup.dateStr, End_Date: dayPopup.dateStr })}>+ Add Event</button>
          </div>
        </div>
      )}

      {dropdownOpenId && (
        <div ref={dropdownRef} className="action-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}>
          <button onClick={() => openEditModal(events.find(e => e._id === dropdownOpenId))}>Edit</button>
          <button className="delete" onClick={() => { setConfirmId(dropdownOpenId); setDropdownOpenId(null); }}>Delete</button>
        </div>
      )}

      {modalType === "view" && selectedEvent && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="acal-modal view-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Event Details</h2>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="view-body">
              <div className="view-header-info">
                <span className={`type-badge ${selectedEvent.Event_Type.toLowerCase()}`}>{selectedEvent.Event_Type}</span>
                <h2>{selectedEvent.Event_Title}</h2>
                <span className={`status-tag ${selectedEvent.Status.toLowerCase()}`}>{selectedEvent.Status}</span>
              </div>
              {selectedEvent.Image_URL && (
                <div style={{ width: '100%', maxHeight: '220px', overflow: 'hidden', borderRadius: '10px', marginBottom: '10px' }}>
                  <img src={selectedEvent.Image_URL} alt={selectedEvent.Event_Title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
              )}
              <div className="view-grid">
                <div className="view-item"><span>Start Date</span><strong>{formatDisplay(selectedEvent.Start_Date)}</strong></div>
                <div className="view-item"><span>End Date</span><strong>{selectedEvent.End_Date ? formatDisplay(selectedEvent.End_Date) : "Same Day"}</strong></div>
                <div className="view-item full"><span>Location</span><strong>{selectedEvent.Location || "Not specified"}</strong></div>
                <div className="view-item full"><span>Description</span><p>{selectedEvent.Description || "No description provided."}</p></div>
              </div>
              <div className="view-footer-actions">
                <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
                <button type="button" className="btn btn-primary" onClick={() => openEditModal(selectedEvent)}>Edit Event</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="acal-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalType === "add" ? "Add Event" : "Edit Event"}</h2>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form className="modal-form" onSubmit={handleSave}>
              <div className="form-section-label">General Details</div>
              <div className="form-group full">
                <label>Event Title *</label>
                <input 
                  type="text"
                  value={formData.Event_Title || ""} 
                  onChange={e => setFormData({...formData, Event_Title: e.target.value})} 
                  placeholder="e.g. Annual Sports Meet 2024"
                  required 
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Event Type</label>
                  <select value={formData.Event_Type || ""} onChange={e => setFormData({...formData, Event_Type: e.target.value})}>
                    {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={formData.Status || ""} onChange={e => setFormData({...formData, Status: e.target.value})}>
                    <option value="Upcoming">Upcoming</option>
                    <option value="Ongoing">Ongoing</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="form-section-label">Schedule & Location</div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input type="date" value={formData.Start_Date || ""} onChange={e => setFormData({...formData, Start_Date: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" value={formData.End_Date || ""} onChange={e => setFormData({...formData, End_Date: e.target.value})} />
                </div>
              </div>
              <div className="form-group full">
                <label>Location</label>
                <input 
                  type="text" 
                  value={formData.Location || ""} 
                  onChange={e => setFormData({...formData, Location: e.target.value})} 
                  placeholder="e.g. College Auditorium"
                />
              </div>

              <div className="form-section-label">Additional Information</div>
              <div className="form-group full">
                <label>Description</label>
                <textarea 
                  value={formData.Description || ""} 
                  onChange={e => setFormData({...formData, Description: e.target.value})} 
                  rows="3"
                  placeholder="Enter event details..."
                />
              </div>

              <div className="form-section-label">Visual Assets</div>
              <div className="form-group full">
                <label>Event Image</label>
                <div className="pg-photo-upload">
                  {(previewUrl || formData.Image_URL) ? (
                    <div className="pg-photo-preview">
                      <img src={previewUrl || formData.Image_URL} alt="Preview" />
                      <div className="pg-photo-actions">
                        <label className="pg-change-btn">
                          Change Image
                          <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                        </label>
                        <button type="button" className="pg-remove-btn" onClick={removePhoto}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <label className="pg-upload-placeholder">
                      <div className="pg-upload-icon">🖼️</div>
                      <span>Select Event Image to Upload</span>
                      <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                    </label>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Event</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete this event?</h3>
            <div className="confirm-actions"><button onClick={() => setConfirmId(null)}>Cancel</button><button onClick={() => handleDelete(confirmId)} className="btn-danger">Delete</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicCalendar;